import {
  useGetDomainClusters,
  useCreateCluster,
  useDeleteCluster,
  useGetClusterPages,
  useMovePageToCluster,
  useGetRadialClusters,
  useGetDomain,
  useGetDomainPages,
  useUpdateCluster,
  useMergeClusters,
  useSaveAndRecrawl,
  useCreateClusterFromPages,
  useSetPageFeatured,
  getGetDomainClustersQueryKey,
  getGetClusterPagesQueryKey,
  getGetRadialClustersQueryKey,
  getGetDomainPagesQueryKey,
  getGetDomainQueryKey,
  type Cluster,
  type ClusterPage,
} from "@workspace/api-client-react";
import { useParams } from "wouter";
import { ClusterTreeVertical } from "@/components/clusters/cluster-tree-vertical";
import { ClusterBoard } from "@/components/clusters/ClusterBoard";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useCrawlCompletionToast, crawlStatusRefetchInterval } from "@/hooks/use-crawl-completion-toast";
import {
  Loader2,
  Plus,
  FolderTree,
  Trash2,
  Crown,
  ChevronDown,
  ChevronRight,
  GripVertical,
  FileText,
  Pencil,
  Combine,
  Save,
  Check,
  X,
  CornerDownRight,
  Move,
  Star,
  Search,
  Filter,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useMemo, useState, useCallback } from "react";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

const formSchema = z.object({
  name: z.string().min(1, "Cluster adı boş olamaz"),
});

const MAX_DEPTH = 20;

const PALETTE: Array<{ accent: string; soft: string; ring: string }> = [
  { accent: "#e83e3c", soft: "#fdecea", ring: "rgba(232,62,60,0.35)" },
  { accent: "#ec6461", soft: "#fef4f3", ring: "rgba(236,100,97,0.35)" },
  { accent: "#b12220", soft: "#fbd9d7", ring: "rgba(177,34,32,0.35)" },
  { accent: "#f18e8b", soft: "#fef4f3", ring: "rgba(241,142,139,0.35)" },
  { accent: "#d42b29", soft: "#fdecea", ring: "rgba(212,43,41,0.35)" },
  { accent: "#911d1c", soft: "#fbd9d7", ring: "rgba(145,29,28,0.35)" },
  { accent: "#ea514f", soft: "#fef4f3", ring: "rgba(234,81,79,0.35)" },
  { accent: "#771e1d", soft: "#fdecea", ring: "rgba(119,30,29,0.35)" },
  { accent: "#f0817e", soft: "#fef4f3", ring: "rgba(240,129,126,0.35)" },
  { accent: "#70706c", soft: "#f4f4f2", ring: "rgba(112,112,108,0.35)" },
];

type ActiveDrag =
  | { kind: "page"; pageId: number; sourceClusterId: number | null; label: string }
  | { kind: "cluster"; clusterId: number; currentParentId: number | null; currentLang: string | null; currentName: string; label: string };

interface DraggablePageProps {
  page: { id: number; url: string; title?: string | null; score?: number | null; isPillar?: boolean; isFeatured?: boolean; incomingLinkCount?: number; outgoingLinkCount?: number };
  clusterId: number | null;
  onSetPillar?: () => void;
  isCurrentPillar?: boolean;
  isSelected: boolean;
  onSelectToggle: (checked: boolean) => void;
  onToggleFeatured?: () => void;
  featuredPending?: boolean;
}

function DraggablePageRow({
  page,
  clusterId,
  onSetPillar,
  isCurrentPillar,
  isSelected,
  onSelectToggle,
  onToggleFeatured,
  featuredPending,
}: DraggablePageProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `page-${page.id}`,
    data: {
      kind: "page",
      pageId: page.id,
      sourceClusterId: clusterId,
      label: page.title || page.url,
    },
  });

  return (
    <div
      ref={setNodeRef}
      className={`flex items-center gap-3 px-3 py-2 rounded-md border bg-card hover:border-primary/40 transition-colors ${
        isDragging ? "opacity-30" : ""
      } ${isSelected ? "ring-2 ring-primary/50 border-primary/40" : ""}`}
      data-testid={`draggable-page-${page.id}`}
    >
      <Checkbox
        checked={isSelected}
        onCheckedChange={(v) => onSelectToggle(v === true)}
        aria-label="Sayfayı seç (alt-küme oluşturmak için)"
        data-testid={`page-select-${page.id}`}
      />
      <button
        type="button"
        {...listeners}
        {...attributes}
        className="cursor-grab active:cursor-grabbing rounded p-0.5 -m-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        aria-label="Sayfayı sürükle"
        title="Sayfayı sürükle (başka cluster üzerine bırak)"
      >
        <GripVertical className="w-4 h-4 shrink-0" />
      </button>
      {page.isPillar ? (
        <Crown className="w-4 h-4 text-amber-500 shrink-0" aria-label="Pillar Sayfa" />
      ) : (
        <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate flex items-center gap-1.5">
          <span className="truncate">{page.title || page.url}</span>
          {page.isFeatured ? (
            <Badge variant="secondary" className="bg-amber-100 text-amber-900 border-amber-300 text-[10px] px-1.5 py-0 shrink-0">
              ÖNE ÇIKAN
            </Badge>
          ) : null}
        </div>
        <div className="text-xs text-muted-foreground truncate">{page.url}</div>
      </div>
      <div className="flex items-center gap-3 text-xs text-muted-foreground shrink-0">
        {typeof page.incomingLinkCount === "number" && (
          <span title="Gelen iç linkler">← {page.incomingLinkCount}</span>
        )}
        {typeof page.outgoingLinkCount === "number" && (
          <span title="Giden iç linkler">→ {page.outgoingLinkCount}</span>
        )}
        {page.score !== null && page.score !== undefined && (
          <Badge variant={page.score >= 85 ? "default" : page.score >= 55 ? "secondary" : "destructive"}>
            {page.score}
          </Badge>
        )}
        {onToggleFeatured && (
          <Button
            variant={page.isFeatured ? "default" : "ghost"}
            size="sm"
            onClick={onToggleFeatured}
            disabled={featuredPending}
            className={`h-7 px-2 text-xs ${page.isFeatured ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}`}
            data-testid={`btn-toggle-featured-${page.id}`}
            title={page.isFeatured ? "Öne çıkarmadan kaldır" : "Bu sayfayı öne çıkar (AI kendi cluster'ında önceliklendirir)"}
          >
            <Star className={`w-3 h-3 ${page.isFeatured ? "fill-current" : ""}`} />
          </Button>
        )}
        {onSetPillar && (
          <Button
            variant={isCurrentPillar ? "default" : "ghost"}
            size="sm"
            onClick={onSetPillar}
            disabled={isCurrentPillar}
            className="h-7 px-2 text-xs"
            data-testid={`btn-set-pillar-${page.id}`}
            title="Bu sayfayı pillar olarak işaretle"
          >
            <Crown className="w-3 h-3 mr-1" />
            {isCurrentPillar ? "Pillar" : "Pillar Yap"}
          </Button>
        )}
      </div>
    </div>
  );
}

interface DroppableClusterProps {
  cluster: ClusterWithChildren;
  isExpanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
  onRename: (newName: string) => Promise<void>;
  onSetPillar: (pageId: number | null) => Promise<void>;
  onAddSubCluster?: () => void;
  domainId: number;
  selected: boolean;
  onSelectChange: (checked: boolean) => void;
  selectedPageIds: Set<number>;
  onTogglePageSelect: (pageId: number, checked: boolean) => void;
  onToggleFeatured: (pageId: number, isFeatured: boolean) => void;
  featuredPendingPageId: number | null;
  depth: number;
  paletteIdx: number;
  renderChildren?: () => React.ReactNode;
  activeDrag: ActiveDrag | null;
}

function DroppableClusterCard({
  cluster,
  isExpanded,
  onToggle,
  onDelete,
  onRename,
  onSetPillar,
  onAddSubCluster,
  domainId: _domainId,
  selected,
  onSelectChange,
  selectedPageIds,
  onTogglePageSelect,
  onToggleFeatured,
  featuredPendingPageId,
  depth,
  paletteIdx,
  renderChildren,
  activeDrag,
}: DroppableClusterProps) {
  const childCount = cluster.children?.length ?? 0;
  const isSub = depth > 0;
  const palette = PALETTE[paletteIdx % PALETTE.length];

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `cluster-${cluster.id}`,
    data: { kind: "cluster-zone", clusterId: cluster.id },
  });

  const {
    attributes: dragAttr,
    listeners: dragListeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: `cluster-drag-${cluster.id}`,
    data: {
      kind: "cluster",
      clusterId: cluster.id,
      currentParentId: cluster.parentId ?? null,
      label: cluster.name,
    },
  });

  const { data: pages, isLoading: pagesLoading } = useGetClusterPages(cluster.id, {
    query: { enabled: isExpanded, queryKey: getGetClusterPagesQueryKey(cluster.id) },
  });

  const pillar = pages?.find((p) => p.isPillar);
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(cluster.name);

  const startEdit = () => {
    setDraftName(cluster.name);
    setEditing(true);
  };
  const submitEdit = async () => {
    const trimmed = draftName.trim();
    if (!trimmed || trimmed === cluster.name) {
      setEditing(false);
      return;
    }
    try {
      await onRename(trimmed);
      setEditing(false);
    } catch {
      // toast handled upstream
    }
  };

  const draggedClusterId = activeDrag?.kind === "cluster" ? activeDrag.clusterId : null;
  const isSelfDragTarget = draggedClusterId === cluster.id;
  const showDropHighlight = isOver && !isSelfDragTarget;

  return (
    <div className="space-y-3">
      <div
        ref={setDropRef}
        className={`rounded-lg border bg-card transition-all ${
          showDropHighlight
            ? "border-primary bg-primary/5 ring-2 ring-primary/30"
            : isSub
            ? "border-l-4"
            : "border-border"
        } ${isDragging ? "opacity-40" : ""}`}
        style={{
          marginLeft: depth * 20,
          ...(isSub && !showDropHighlight
            ? {
                borderLeftColor: palette.accent,
                background: `linear-gradient(90deg, ${palette.soft} 0%, hsl(var(--card)) 24%)`,
              }
            : {}),
        }}
        data-testid={`cluster-card-${cluster.id}`}
      >
        <div className="flex items-center gap-2 p-4">
          <Checkbox
            checked={selected}
            onCheckedChange={(v) => onSelectChange(v === true)}
            aria-label="Cluster'ı birleştirmek için seç"
            data-testid={`cluster-select-${cluster.id}`}
          />
          <button
            type="button"
            ref={setDragRef}
            {...dragListeners}
            {...dragAttr}
            className="cursor-grab active:cursor-grabbing rounded px-1 py-1 -mx-1 -my-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label={`Cluster'ı sürükle: ${cluster.name}`}
            title="Cluster'ı sürükle (başka cluster üzerine bırakırsan alt-küme olur, kök seviyeye bırakırsan üst seviyeye çıkar)"
            data-testid={`cluster-drag-${cluster.id}`}
          >
            <GripVertical className="w-4 h-4" />
          </button>
          <button
            onClick={onToggle}
            className="flex items-center gap-3 text-left flex-1 min-w-0"
            data-testid={`cluster-toggle-${cluster.id}`}
          >
            {isExpanded ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
            {isSub ? (
              <CornerDownRight className="w-4 h-4 shrink-0" style={{ color: palette.accent }} />
            ) : (
              <FolderTree className="w-5 h-5 shrink-0" style={{ color: palette.accent }} />
            )}
            <div className="flex-1 min-w-0">
              {editing ? (
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <Input
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") submitEdit();
                      else if (e.key === "Escape") setEditing(false);
                    }}
                    className="h-8"
                    autoFocus
                    data-testid={`cluster-rename-input-${cluster.id}`}
                  />
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={submitEdit} data-testid={`cluster-rename-save-${cluster.id}`}>
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="font-medium flex items-center gap-2 flex-wrap">
                  <span className="truncate">{cluster.name}</span>
                  {isSub && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                      Alt-küme
                    </Badge>
                  )}
                  {cluster.createdByClaude && (
                    <Badge variant="secondary" className="text-xs">
                      AI
                    </Badge>
                  )}
                  {(cluster.pillarPageTitle || pillar) && (
                    <Badge variant="outline" className="text-xs flex items-center gap-1">
                      <Crown className="w-3 h-3 text-amber-500" />
                      Pillar:{" "}
                      <span className="truncate max-w-[180px]">{pillar?.title ?? cluster.pillarPageTitle}</span>
                    </Badge>
                  )}
                </div>
              )}
              <div className="text-xs text-muted-foreground mt-1 flex gap-4">
                <span>{cluster.pageCount} Sayfa</span>
                {cluster.avgScore !== null && cluster.avgScore !== undefined && (
                  <span>Ort. Skor: {Math.round(cluster.avgScore)}</span>
                )}
                {childCount > 0 && <span>{childCount} alt-küme</span>}
              </div>
            </div>
          </button>
          {!editing && (
            <Button
              variant="ghost"
              size="icon"
              onClick={startEdit}
              data-testid={`cluster-rename-${cluster.id}`}
              title="Yeniden adlandır"
            >
              <Pencil className="w-4 h-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="text-destructive hover:text-destructive/90 hover:bg-destructive/10"
            onClick={onDelete}
            data-testid={`cluster-delete-${cluster.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>

        {isExpanded && (
          <div className="border-t p-3 space-y-3 bg-muted/20">
            {pagesLoading ? (
              <div className="text-sm text-muted-foreground text-center py-4">Yükleniyor...</div>
            ) : pages && pages.length > 0 ? (
              <div className="space-y-2">
                {pages.map((page) => (
                  <DraggablePageRow
                    key={page.id}
                    page={page}
                    clusterId={cluster.id}
                    isCurrentPillar={!!pillar && pillar.id === page.id}
                    onSetPillar={() => onSetPillar(page.id)}
                    isSelected={selectedPageIds.has(page.id)}
                    onSelectToggle={(checked) => onTogglePageSelect(page.id, checked)}
                    onToggleFeatured={() => onToggleFeatured(page.id, !page.isFeatured)}
                    featuredPending={featuredPendingPageId === page.id}
                  />
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground text-center py-4">
                Bu cluster'da henüz sayfa yok. Sayfaları buraya sürükleyebilirsiniz.
              </div>
            )}

            {onAddSubCluster && (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onAddSubCluster}
                      data-testid={`cluster-add-sub-${cluster.id}`}
                      className="w-full justify-center gap-2 border-dashed"
                    >
                      <Plus className="w-4 h-4" />
                      Alt-küme ekle {childCount > 0 ? `(${childCount})` : ""}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    Yeni alt-küme oluştur veya mevcut bir cluster'ı bu cluster'ın altına taşı (sürükle-bırak da yapabilirsin).
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}
      </div>
      {renderChildren?.()}
    </div>
  );
}

function UnassignedDropZone({ children }: { children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `cluster-unassigned`,
    data: { kind: "cluster-zone", clusterId: null },
  });
  return (
    <div
      ref={setNodeRef}
      className={`rounded-lg border-2 border-dashed p-3 transition-colors ${
        isOver ? "border-primary bg-primary/5" : "border-border bg-muted/20"
      }`}
      data-testid="cluster-unassigned"
    >
      <div className="text-xs font-medium text-muted-foreground mb-2">
        Atanmamış / Kök seviye — bir sayfayı sürüklersen cluster'dan çıkarılır, bir alt-kümeyi sürüklersen üst seviyeye çıkar.
      </div>
      {children}
    </div>
  );
}

type ClusterWithChildren = Cluster & { children?: ClusterWithChildren[] };

// ─── Aranabilir cluster seçici (orphan sayfaları için) ───────────────────────
function OrphanClusterSelect({
  clusters,
  value,
  onChange,
  pageId,
}: {
  clusters: ClusterWithChildren[];
  value: string;
  onChange: (v: string) => void;
  pageId: number;
}) {
  const [search, setSearch] = useState("");
  const filtered = useMemo(() => {
    if (!search.trim()) return clusters;
    const q = search.toLowerCase();
    return clusters.filter((c) =>
      c.name.toLowerCase().includes(q) ||
      c.name.replace(/^\[[A-Z]{2}\] /, "").toLowerCase().includes(q)
    );
  }, [clusters, search]);

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-[200px] text-xs" data-testid={`orphan-cluster-select-${pageId}`}>
        <SelectValue placeholder="Cluster'a ata..." />
      </SelectTrigger>
      <SelectContent className="p-0">
        <div className="px-2 pt-2 pb-1 sticky top-0 bg-popover z-10 border-b">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
            <input
              className="w-full pl-6 pr-2 py-1 text-xs rounded border bg-background outline-none focus:ring-1 focus:ring-primary"
              placeholder="Cluster ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
        <div className="max-h-48 overflow-y-auto">
          {filtered.map((c) => (
            <SelectItem key={c.id} value={String(c.id)}>
              {c.parentId != null ? "↳ " : ""}{c.name.replace(/^\[[A-Z]{2}\] /, "")}
            </SelectItem>
          ))}
          {filtered.length === 0 && (
            <div className="text-xs text-muted-foreground text-center py-3">Sonuç yok</div>
          )}
        </div>
      </SelectContent>
    </Select>
  );
}

export default function DomainClusters() {
  const { id } = useParams<{ id: string }>();
  const domainId = parseInt(id, 10);

  const { data: clusters, isLoading } = useGetDomainClusters(domainId, {
    query: { enabled: !!domainId, queryKey: getGetDomainClustersQueryKey(domainId) },
  });
  const { data: radial } = useGetRadialClusters(domainId, {
    query: { enabled: !!domainId, queryKey: getGetRadialClustersQueryKey(domainId) },
  });
  const { data: domain } = useGetDomain(domainId, {
    query: {
      enabled: !!domainId,
      queryKey: getGetDomainQueryKey(domainId),
      refetchInterval: crawlStatusRefetchInterval,
    },
  });
  useCrawlCompletionToast({
    domainId,
    status: domain?.status,
    domainUrl: domain?.url,
    totalPages: domain?.totalPages,
    averageScore: domain?.averageScore,
  });

  const createCluster = useCreateCluster();
  const deleteCluster = useDeleteCluster();
  const updateCluster = useUpdateCluster();
  const mergeClusters = useMergeClusters();
  const saveAndRecrawl = useSaveAndRecrawl();
  const movePage = useMovePageToCluster();
  const createClusterFromPages = useCreateClusterFromPages();
  const setPageFeatured = useSetPageFeatured();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [activeDrag, setActiveDrag] = useState<ActiveDrag | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeName, setMergeName] = useState("");

  const [addSubFor, setAddSubFor] = useState<ClusterWithChildren | null>(null);
  const [addSubMode, setAddSubMode] = useState<"new" | "existing">("new");
  const [addSubName, setAddSubName] = useState("");
  const [addSubPick, setAddSubPick] = useState<string>("");

  const [selectedPageIds, setSelectedPageIds] = useState<Set<number>>(new Set());
  const [pageMergeOpen, setPageMergeOpen] = useState(false);
  const [pageMergeName, setPageMergeName] = useState("");
  const [pageMergeParentId, setPageMergeParentId] = useState<string>("");

  const [orphansOpen, setOrphansOpen] = useState(false);
  const [orphanAssignTarget, setOrphanAssignTarget] = useState<Record<number, string>>({});
  const [orphanSearch, setOrphanSearch] = useState("");
  const [orphanPage, setOrphanPage] = useState(0);
  const ORPHAN_PAGE_SIZE = 50;
  const [globalPageSearch, setGlobalPageSearch] = useState("");
  // ── Neural map: arama + filtre + odak ──
  const [focusId, setFocusId] = useState<number | null>(null);
  const [focusPageId, setFocusPageId] = useState<number | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState("");
  type NeuralPage = { id: number; url: string; title?: string | null; isPillar: boolean };
  const [neuralPages, setNeuralPages] = useState<Map<number, NeuralPage[]>>(new Map());
  const handlePagesLoaded = useCallback((m: Map<number, NeuralPage[]>) => setNeuralPages(m), []);
  const [deleteClusterId, setDeleteClusterId] = useState<number | null>(null);
  const [recrawlOpen, setRecrawlOpen] = useState(false);
  const [featuredPendingPageId, setFeaturedPendingPageId] = useState<number | null>(null);
  const [activeView, setActiveView] = useState<"neural" | "tree">("neural");

  const { data: domainData } = useGetDomain(domainId);

  const handleToggleFeatured = async (pageId: number, isFeatured: boolean) => {
    setFeaturedPendingPageId(pageId);
    try {
      await setPageFeatured.mutateAsync({ pageId, data: { isFeatured } });
      toast({ title: isFeatured ? "Sayfa öne çıkarıldı" : "Öne çıkarma kaldırıldı" });
      invalidateAll();
    } catch {
      toast({ title: "İşlem başarısız", variant: "destructive" });
    } finally {
      setFeaturedPendingPageId(null);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const tree = useMemo<ClusterWithChildren[]>(
    () => (clusters as ClusterWithChildren[] | undefined) ?? [],
    [clusters],
  );

  const allClusters = useMemo<ClusterWithChildren[]>(() => {
    const out: ClusterWithChildren[] = [];
    const walk = (nodes: ClusterWithChildren[]) => {
      for (const n of nodes) {
        out.push(n);
        if (n.children && n.children.length > 0) walk(n.children);
      }
    };
    walk(tree);
    return out;
  }, [tree]);

  // ── Arama / filtre veri kaynağı ──
  const stripLang = (n: string) => n.replace(/^\[[A-Z]{2}\] /, "");
  const clusterLang = (n: string) => n.match(/^\[([A-Z]{2})\] /)?.[1] ?? null;
  // Arama için domain'in TÜM sayfaları (tek seferde, lazy fetch'e bağlı değil)
  const { data: allDomainPages } = useGetDomainPages(
    domainId,
    {},
    { query: { enabled: !!domainId, queryKey: getGetDomainPagesQueryKey(domainId, {}) } },
  );

  type SearchHit = { kind: "cluster" | "page"; id: number; clusterId: number; label: string; sub: string };
  const searchItems = useMemo<SearchHit[]>(() => {
    const items: SearchHit[] = [];
    for (const c of allClusters) items.push({ kind: "cluster", id: c.id, clusterId: c.id, label: stripLang(c.name), sub: "Cluster" });
    for (const pg of (allDomainPages ?? [])) {
      if (pg.clusterId == null) continue; // orphan sayfalar odaklanamaz
      const raw = pg.title ?? (pg.url.replace(/^https?:\/\/[^/]+/, "") || pg.url);
      items.push({ kind: "page", id: pg.id, clusterId: pg.clusterId, label: raw || pg.url, sub: "Sayfa" });
    }
    return items;
  }, [allClusters, allDomainPages]);

  const searchSuggestions = useMemo(() => {
    const q = globalPageSearch.trim().toLowerCase();
    if (q.length < 2) return [];
    const matches = searchItems.filter((it) => it.label.toLowerCase().includes(q));
    // cluster ve sayfaları ayrı sınırla ki ikisi de görünsün
    const clusters = matches.filter((m) => m.kind === "cluster").slice(0, 6);
    const pages = matches.filter((m) => m.kind === "page").slice(0, 30);
    return [...clusters, ...pages];
  }, [globalPageSearch, searchItems]);

  // Filtre listesi: clusterlar (dil + isim), arama ile daraltılabilir
  const filterClusters = useMemo(() => {
    const q = filterQuery.trim().toLowerCase();
    return allClusters
      .map((c) => ({ id: c.id, label: stripLang(c.name), lang: clusterLang(c.name) }))
      .filter((c) => !q || c.label.toLowerCase().includes(q));
  }, [allClusters, filterQuery]);

  const pickSearch = (it: SearchHit) => {
    setActiveView("neural");
    setFocusId(it.clusterId);
    setFocusPageId(it.kind === "page" ? it.id : null);
    setGlobalPageSearch(it.label);
    setSearchOpen(false);
  };

  const { data: orphanPages } = useGetDomainPages(
    domainId,
    { clusterId: null },
    {
      query: {
        enabled: !!domainId,
        queryKey: getGetDomainPagesQueryKey(domainId, { clusterId: null }),
      },
    },
  );


  const togglePageSelect = (pageId: number, checked: boolean) => {
    setSelectedPageIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(pageId);
      else next.delete(pageId);
      return next;
    });
  };

  const clearPageSelection = () => setSelectedPageIds(new Set());

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: "" },
  });

  function invalidateAll() {
    queryClient.invalidateQueries({ queryKey: getGetDomainClustersQueryKey(domainId) });
    queryClient.invalidateQueries({ queryKey: getGetRadialClustersQueryKey(domainId) });
    queryClient.invalidateQueries({ queryKey: getGetDomainPagesQueryKey(domainId) });
  }

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      await createCluster.mutateAsync({ domainId, data: { name: values.name } });
      toast({ title: "Cluster oluşturuldu" });
      form.reset();
      invalidateAll();
    } catch {
      toast({ title: "Hata", variant: "destructive" });
    }
  }

  const handleDelete = (clusterId: number) => {
    setDeleteClusterId(clusterId);
  };

  const confirmDelete = async () => {
    if (deleteClusterId == null) return;
    const clusterId = deleteClusterId;
    setDeleteClusterId(null);
    try {
      await deleteCluster.mutateAsync({ clusterId });
      toast({ title: "Cluster silindi" });
      invalidateAll();
      queryClient.invalidateQueries({ queryKey: getGetClusterPagesQueryKey(clusterId) });
    } catch {
      toast({ title: "Hata", variant: "destructive" });
    }
  };

  const handleRename = async (clusterId: number, newName: string) => {
    try {
      await updateCluster.mutateAsync({ clusterId, data: { name: newName } });
      toast({ title: "Cluster yeniden adlandırıldı" });
      invalidateAll();
    } catch {
      toast({ title: "Yeniden adlandırma başarısız", variant: "destructive" });
      throw new Error("rename failed");
    }
  };

  const handleSetPillar = async (clusterId: number, pageId: number | null) => {
    try {
      await updateCluster.mutateAsync({ clusterId, data: { pillarPageId: pageId } });
      toast({ title: "Pillar sayfa ayarlandı" });
      invalidateAll();
      queryClient.invalidateQueries({ queryKey: getGetClusterPagesQueryKey(clusterId) });
    } catch {
      toast({ title: "Pillar ayarlanamadı", variant: "destructive" });
    }
  };

  const toggleExpand = (clusterId: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(clusterId)) next.delete(clusterId);
      else next.add(clusterId);
      return next;
    });
  };

  const toggleSelect = (clusterId: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(clusterId);
      else next.delete(clusterId);
      return next;
    });
  };

  const openMerge = () => {
    if (selectedIds.size < 2) {
      toast({ title: "En az iki cluster seçmelisin", variant: "destructive" });
      return;
    }
    const sel = allClusters.filter((c) => selectedIds.has(c.id));
    setMergeName(sel.map((c) => c.name).join(" + "));
    setMergeOpen(true);
  };

  const submitMerge = async () => {
    if (!mergeName.trim()) {
      toast({ title: "Yeni cluster için isim ver", variant: "destructive" });
      return;
    }
    try {
      await mergeClusters.mutateAsync({
        data: { clusterIds: Array.from(selectedIds), name: mergeName.trim() },
      });
      toast({ title: "Cluster'lar birleştirildi" });
      setMergeOpen(false);
      setSelectedIds(new Set());
      invalidateAll();
    } catch {
      toast({ title: "Birleştirme başarısız", variant: "destructive" });
    }
  };

  const handleSaveRecrawl = () => {
    setRecrawlOpen(true);
  };

  const confirmSaveRecrawl = async () => {
    setRecrawlOpen(false);
    try {
      await saveAndRecrawl.mutateAsync({ domainId });
      toast({ title: "Cluster yapısı kaydedildi ve yeniden tarama başlatıldı" });
      queryClient.invalidateQueries({ queryKey: getGetDomainQueryKey(domainId) });
      invalidateAll();
    } catch {
      toast({ title: "Yeniden tarama başlatılamadı", variant: "destructive" });
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current as ActiveDrag | undefined;
    if (data) setActiveDrag(data);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const dragData = event.active.data.current as ActiveDrag | undefined;
    const dropData = event.over?.data.current as
      | { kind: "cluster-zone"; clusterId: number | null }
      | { kind: "lang-zone"; lang: string | null }
      | undefined;
    setActiveDrag(null);
    if (!dragData || !event.over || !dropData) return;

    if (dragData.kind === "page") {
      if (dropData.kind !== "cluster-zone") return;
      if (dropData.clusterId === dragData.sourceClusterId) return;
      const targetClusterId = dropData.clusterId;
      try {
        await movePage.mutateAsync({
          pageId: dragData.pageId,
          data: { clusterId: targetClusterId },
        });
        toast({ title: "Sayfa taşındı" });
        invalidateAll();
        if (dragData.sourceClusterId != null) {
          queryClient.invalidateQueries({ queryKey: getGetClusterPagesQueryKey(dragData.sourceClusterId) });
        }
        if (targetClusterId) {
          queryClient.invalidateQueries({ queryKey: getGetClusterPagesQueryKey(targetClusterId) });
        }
        queryClient.invalidateQueries({
          queryKey: getGetDomainPagesQueryKey(domainId, { clusterId: null }),
        });
        setSelectedPageIds((prev) => {
          if (!prev.has(dragData.pageId)) return prev;
          const next = new Set(prev);
          next.delete(dragData.pageId);
          return next;
        });
      } catch {
        toast({ title: "Sayfa taşınamadı", variant: "destructive" });
      }
      return;
    }

    if (dragData.kind === "cluster") {
      // Cluster dropped onto a language column → change language prefix
      if (dropData.kind === "lang-zone") {
        const toLang = dropData.lang;
        if (toLang === dragData.currentLang) return;
        const displayName = dragData.currentName.replace(/^\[[A-Z]{2}\] /, "");
        const newName = toLang ? `[${toLang}] ${displayName}` : displayName;
        try {
          await updateCluster.mutateAsync({ clusterId: dragData.clusterId, data: { name: newName } });
          toast({ title: `Cluster "${displayName}" ${toLang ?? "kök"} diline taşındı` });
          invalidateAll();
        } catch {
          toast({ title: "Cluster taşınamadı", variant: "destructive" });
        }
        return;
      }

      if (dropData.kind !== "cluster-zone") return;
      const newParentId = dropData.clusterId;
      if (newParentId === dragData.clusterId) return;
      if (newParentId === dragData.currentParentId) return;
      try {
        await updateCluster.mutateAsync({
          clusterId: dragData.clusterId,
          data: { parentId: newParentId },
        });
        toast({
          title:
            newParentId === null
              ? "Cluster kök seviyeye taşındı"
              : "Cluster alt-küme olarak ayarlandı",
        });
        invalidateAll();
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        const detail = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        toast({
          title: detail || (status === 400 ? "Cluster taşınamadı (kural ihlali)" : "Cluster taşınamadı"),
          variant: "destructive",
        });
      }
    }
  };

  const openAddSubDialog = (parent: ClusterWithChildren) => {
    setAddSubFor(parent);
    setAddSubMode("new");
    setAddSubName("");
    setAddSubPick("");
  };

  const submitAddSub = async () => {
    if (!addSubFor) return;
    const parent = addSubFor;

    try {
      if (addSubMode === "new") {
        const trimmed = addSubName.trim();
        if (!trimmed) {
          toast({ title: "İsim girmelisin", variant: "destructive" });
          return;
        }
        await createCluster.mutateAsync({
          domainId,
          data: { name: trimmed, parentId: parent.id },
        });
        toast({ title: "Alt-küme oluşturuldu" });
      } else {
        const pickId = parseInt(addSubPick, 10);
        if (!pickId) {
          toast({ title: "Bir cluster seçmelisin", variant: "destructive" });
          return;
        }
        await updateCluster.mutateAsync({
          clusterId: pickId,
          data: { parentId: parent.id },
        });
        toast({ title: "Cluster alt-küme olarak ayarlandı" });
      }
      setExpanded((prev) => {
        const next = new Set(prev);
        next.add(parent.id);
        return next;
      });
      setAddSubFor(null);
      invalidateAll();
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const detail = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast({
        title: detail || (status === 400 ? "Eklenemedi (kural ihlali)" : "Eklenemedi"),
        variant: "destructive",
      });
    }
  };

  const renderTree = (
    nodes: ClusterWithChildren[],
    depth: number,
    parentPaletteIdx: number | null,
  ): React.ReactNode => {
    return nodes.map((c, i) => {
      const paletteIdx = depth === 0 ? i : parentPaletteIdx ?? 0;
      return (
        <DroppableClusterCard
          key={c.id}
          cluster={c}
          isExpanded={expanded.has(c.id)}
          onToggle={() => toggleExpand(c.id)}
          onDelete={() => handleDelete(c.id)}
          onRename={(newName) => handleRename(c.id, newName)}
          onSetPillar={(pageId) => handleSetPillar(c.id, pageId)}
          onAddSubCluster={() => openAddSubDialog(c)}
          domainId={domainId}
          selected={selectedIds.has(c.id)}
          onSelectChange={(v) => toggleSelect(c.id, v)}
          selectedPageIds={selectedPageIds}
          onTogglePageSelect={togglePageSelect}
          onToggleFeatured={handleToggleFeatured}
          featuredPendingPageId={featuredPendingPageId}
          depth={Math.min(depth, MAX_DEPTH - 1)}
          paletteIdx={paletteIdx}
          activeDrag={activeDrag}
          renderChildren={
            c.children && c.children.length > 0
              ? () => <div className="space-y-3">{renderTree(c.children!, depth + 1, paletteIdx)}</div>
              : undefined
          }
        />
      );
    });
  };

  // Map page id -> source cluster id (for selected cluster pages). Orphans are not in this map.
  const pageClusterMap = useMemo(() => {
    const map = new Map<number, number | null>();
    for (const p of orphanPages ?? []) map.set(p.id, null);
    // We don't have all cluster pages eagerly; rely on what useGetClusterPages returns via cache.
    for (const c of allClusters) {
      const cached = queryClient.getQueryData<ClusterPage[]>(getGetClusterPagesQueryKey(c.id));
      if (cached) {
        for (const p of cached) map.set(p.id, c.id);
      }
    }
    return map;
  }, [orphanPages, allClusters, queryClient, selectedPageIds]);

  const sharedParentForSelection = useMemo(() => {
    if (selectedPageIds.size === 0) return null;
    let shared: number | null | undefined = undefined;
    for (const pid of selectedPageIds) {
      const cid = pageClusterMap.get(pid);
      if (cid === undefined) {
        // Unknown cluster (page rows not yet fetched) → can't assume shared parent.
        return null;
      }
      if (shared === undefined) shared = cid;
      else if (shared !== cid) return null;
    }
    return shared ?? null;
  }, [selectedPageIds, pageClusterMap]);

  const openPageMergeDialog = () => {
    if (selectedPageIds.size < 2) {
      toast({ title: "En az iki sayfa seç", variant: "destructive" });
      return;
    }
    setPageMergeName("");
    setPageMergeParentId(sharedParentForSelection ? String(sharedParentForSelection) : "__root");
    setPageMergeOpen(true);
  };

  const submitPageMerge = async () => {
    const name = pageMergeName.trim();
    if (!name) {
      toast({ title: "Yeni alt-küme için isim ver", variant: "destructive" });
      return;
    }
    const parentId =
      pageMergeParentId === "__root" || pageMergeParentId === ""
        ? null
        : parseInt(pageMergeParentId, 10);
    const sourceClusterIds = new Set<number>();
    const movedIds = Array.from(selectedPageIds);
    for (const pid of movedIds) {
      const src = pageClusterMap.get(pid);
      if (src != null) sourceClusterIds.add(src);
    }
    try {
      const created = await createClusterFromPages.mutateAsync({
        domainId,
        data: { name, parentId, pageIds: movedIds },
      });
      toast({
        title: `${movedIds.length} sayfa "${created.name}" alt-kümesine taşındı`,
      });
      clearPageSelection();
      setPageMergeOpen(false);
      invalidateAll();
      queryClient.invalidateQueries({ queryKey: getGetClusterPagesQueryKey(created.id) });
      for (const cid of sourceClusterIds) {
        queryClient.invalidateQueries({ queryKey: getGetClusterPagesQueryKey(cid) });
      }
      queryClient.invalidateQueries({
        queryKey: getGetDomainPagesQueryKey(domainId, { clusterId: null }),
      });
      setExpanded((prev) => {
        const next = new Set(prev);
        next.add(created.id);
        if (parentId) next.add(parentId);
        return next;
      });
    } catch (err) {
      const msg =
        err instanceof Error && err.message ? err.message : "Alt-küme oluşturulamadı";
      toast({ title: msg, variant: "destructive" });
    }
  };

  const assignOrphanToCluster = async (pageId: number, targetClusterId: number) => {
    try {
      await movePage.mutateAsync({
        pageId,
        data: { clusterId: targetClusterId },
      });
      toast({ title: "Sayfa cluster'a atandı" });
      invalidateAll();
      queryClient.invalidateQueries({ queryKey: getGetClusterPagesQueryKey(targetClusterId) });
      queryClient.invalidateQueries({
        queryKey: getGetDomainPagesQueryKey(domainId, { clusterId: null }),
      });
      setOrphanAssignTarget((prev) => {
        const next = { ...prev };
        delete next[pageId];
        return next;
      });
      setSelectedPageIds((prev) => {
        if (!prev.has(pageId)) return prev;
        const next = new Set(prev);
        next.delete(pageId);
        return next;
      });
    } catch {
      toast({ title: "Sayfa atanamadı", variant: "destructive" });
    }
  };

  const eligibleExistingClusters = useMemo(() => {
    if (!addSubFor) return [];
    const blocked = new Set<number>();
    blocked.add(addSubFor.id);
    const collectDescendants = (n: ClusterWithChildren) => {
      blocked.add(n.id);
      (n.children ?? []).forEach(collectDescendants);
    };
    collectDescendants(addSubFor);
    return allClusters.filter(
      (c) => !blocked.has(c.id) && c.parentId !== addSubFor.id,
    );
  }, [addSubFor, allClusters]);

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
    {/* ── neurol-ux tam ekran düzeni ── */}
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", background: "#fafaf9" }}>

      {/* ── Header — neurol-ux Header.tsx birebir ── */}
      <header style={{
        height: 56,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: 16,
        borderBottom: "1px solid #e8edf3",
        background: "#ffffff",
        padding: "0 20px",
      }}>
        {/* Project switcher */}
        <button style={{
          display: "flex", alignItems: "center", gap: 8,
          border: "1px solid #e7e7e4", borderRadius: 8,
          background: "#f4f4f2", padding: "5px 12px",
          fontSize: 13, fontWeight: 500, color: "#1d1d1b", cursor: "pointer",
        }}>
          {domainData?.url?.replace(/^https?:\/\//, "").replace(/\/$/, "") ?? "Domain"}
          <ChevronDown size={13} style={{ color: "#70706c" }} />
        </button>

        {/* View toggle (neurol-ux: rounded pill) */}
        <div style={{
          display: "flex", borderRadius: 8,
          border: "1px solid #e7e7e4", background: "#f4f4f2", padding: 2, gap: 2,
        }}>
          {(["neural", "tree"] as const).map((v) => (
            <button key={v} onClick={() => setActiveView(v)} style={{
              borderRadius: 6, padding: "4px 12px",
              fontSize: 11, fontWeight: 600, cursor: "pointer", border: "none",
              transition: "all 0.15s",
              background: activeView === v ? "rgba(139,92,246,0.2)" : "transparent",
              color: activeView === v ? "#e83e3c" : "#70706c",
            }}>
              {v === "neural" ? "Nöral Harita" : "Ağaç Görünümü"}
            </button>
          ))}
        </div>

        <div style={{ flex: 1 }} />

        {/* Search — canlı öneriler */}
        <div style={{ position: "relative" }}>
          <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#70706c" }} />
          <input
            value={globalPageSearch}
            onChange={(e) => { setGlobalPageSearch(e.target.value); setSearchOpen(true); }}
            onFocus={() => setSearchOpen(true)}
            onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
            placeholder="Cluster veya sayfa ara…"
            style={{
              width: 240, border: "1px solid #e7e7e4", borderRadius: 8,
              background: "#f4f4f2", padding: "5px 30px 5px 32px",
              fontSize: 13, color: "#1d1d1b", outline: "none",
            }}
          />
          {globalPageSearch && (
            <button onMouseDown={(e) => { e.preventDefault(); setGlobalPageSearch(""); setFocusId(null); setFocusPageId(null); }}
              title="Temizle"
              style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", border: "none", background: "transparent", color: "#70706c", cursor: "pointer", display: "flex", padding: 2 }}>
              <X size={13} />
            </button>
          )}
          {searchOpen && searchSuggestions.length > 0 && (
            <div style={{
              position: "absolute", top: "calc(100% + 6px)", left: 0, width: 300, zIndex: 60,
              background: "#ffffff", border: "1px solid #e7e7e4", borderRadius: 10,
              boxShadow: "0 8px 28px rgba(0,0,0,0.12)", padding: 4, maxHeight: 340, overflowY: "auto",
            }}>
              {searchSuggestions.map((it) => (
                <button key={`${it.kind}-${it.id}`} onMouseDown={(e) => { e.preventDefault(); pickSearch(it); }}
                  style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
                    border: "none", background: "transparent", cursor: "pointer", borderRadius: 7, padding: "8px 10px", color: "#1d1d1b" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#f4f4f2")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                  <span style={{ fontSize: 14, flexShrink: 0 }}>{it.kind === "cluster" ? "◉" : "▭"}</span>
                  <span style={{ flex: 1, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.label}</span>
                  <span style={{ fontSize: 10, color: "#70706c", flexShrink: 0 }}>{it.sub}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Filter — cluster seç → odakla (alt/üst zinciri filtrelenir) */}
        <div style={{ position: "relative" }}>
          <button onClick={() => setFilterOpen((o) => !o)} style={{
            display: "flex", alignItems: "center", gap: 6,
            border: `1px solid ${focusId != null ? "#e83e3c" : "#e7e7e4"}`, borderRadius: 8,
            padding: "5px 12px", fontSize: 11, fontWeight: 500,
            color: focusId != null ? "#e83e3c" : "#70706c", background: focusId != null ? "rgba(139,92,246,0.12)" : "transparent", cursor: "pointer",
          }}>
            <Filter size={13} />{focusId != null ? "Filtre aktif" : "Filtrele"}
          </button>
          {filterOpen && (
            <div style={{
              position: "absolute", top: "calc(100% + 6px)", right: 0, width: 280, zIndex: 60,
              background: "#ffffff", border: "1px solid #e7e7e4", borderRadius: 10,
              boxShadow: "0 8px 28px rgba(0,0,0,0.12)", padding: 8,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                <input autoFocus value={filterQuery} onChange={(e) => setFilterQuery(e.target.value)}
                  placeholder="Cluster filtrele…"
                  style={{ flex: 1, border: "1px solid #e7e7e4", borderRadius: 7, background: "#f4f4f2",
                    padding: "5px 9px", fontSize: 12, color: "#1d1d1b", outline: "none" }} />
                {focusId != null && (
                  <button onClick={() => { setFocusId(null); setFocusPageId(null); }}
                    style={{ border: "none", background: "#f4f4f2", color: "#70706c", borderRadius: 7, padding: "5px 8px", fontSize: 11, cursor: "pointer" }}>
                    Sıfırla
                  </button>
                )}
              </div>
              <div style={{ maxHeight: 300, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
                {filterClusters.map((c) => (
                  <button key={c.id} onClick={() => { setActiveView("neural"); setFocusId(c.id); setFocusPageId(null); setFilterOpen(false); }}
                    style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left",
                      border: "none", cursor: "pointer", borderRadius: 7, padding: "7px 9px", fontSize: 12,
                      background: focusId === c.id ? "rgba(139,92,246,0.18)" : "transparent",
                      color: focusId === c.id ? "#e83e3c" : "#1d1d1b" }}
                    onMouseEnter={(e) => { if (focusId !== c.id) e.currentTarget.style.background = "#f4f4f2"; }}
                    onMouseLeave={(e) => { if (focusId !== c.id) e.currentTarget.style.background = "transparent"; }}>
                    {c.lang && <span style={{ fontSize: 9, fontWeight: 700, color: "#70706c", border: "1px solid #e7e7e4", borderRadius: 4, padding: "0 4px" }}>{c.lang}</span>}
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.label}</span>
                  </button>
                ))}
                {filterClusters.length === 0 && <p style={{ fontSize: 12, color: "#70706c", padding: "8px 9px", margin: 0 }}>Cluster bulunamadı</p>}
              </div>
            </div>
          )}
        </div>

        {/* Zoom / Undo */}
        {[Loader2, Loader2, Loader2, Loader2].map((_, i) => null)}
        <div style={{ display: "flex", gap: 2 }}>
          {[
            { icon: "−", title: "Zoom Out" },
            { icon: "+", title: "Zoom In"  },
            { icon: "↩", title: "Undo"     },
            { icon: "↪", title: "Redo"     },
          ].map(({ icon, title }) => (
            <button key={title} title={title} style={{
              padding: "5px 7px", borderRadius: 8, border: "none",
              background: "transparent", color: "#70706c", cursor: "pointer", fontSize: 14,
            }}>{icon}</button>
          ))}
        </div>

        {/* Add New */}
        <button
          onClick={handleSaveRecrawl}
          disabled={saveAndRecrawl.isPending}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "#e83e3c", border: "none", borderRadius: 8,
            padding: "6px 16px", fontSize: 13, fontWeight: 600,
            color: "#fff", cursor: "pointer",
          }}
        >
          {saveAndRecrawl.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={15} />}
          Kaydet & Yenile
        </button>
      </header>

      {/* ── Tarama kontrol şeridi — tarama sürerken/durmuşken buradan da yönetilir
            (kullanıcı bug'ı: cluster'a girip dönünce "Devam Et" kayboluyordu) ── */}
      {domain && ["crawling", "linking", "clustering", "analyzing", "stopped", "error"].includes(domain.status ?? "") && (
        <div style={{
          flexShrink: 0, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
          padding: "8px 20px", borderBottom: "1px solid #e8edf3",
          background: domain.status === "stopped" || domain.status === "error" ? "#fff7ed" : "#f5f3ff",
        }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: domain.status === "stopped" || domain.status === "error" ? "#c2410c" : "#d42b29" }}>
            {domain.status === "stopped" ? "Tarama durduruldu" : domain.status === "error" ? "Tarama hata verdi" : `Tarama sürüyor (%${Math.min(100, Math.max(0, domain.crawlProgress ?? 0))})`}
          </span>
          {["crawling", "linking", "clustering", "analyzing"].includes(domain.status ?? "") && (
            <button
              onClick={async () => {
                const r = await fetch(`/api/domains/${domainId}/stop`, { method: "POST" });
                const dd = await r.json().catch(() => ({} as { message?: string; error?: string }));
                toast(r.ok ? { title: "Durduruluyor", description: dd.message } : { title: "Durdurma başarısız", description: dd.error, variant: "destructive" });
                queryClient.invalidateQueries({ queryKey: getGetDomainQueryKey(domainId) });
              }}
              className="bg-white dark:bg-gray-800" style={{ fontSize: 12, fontWeight: 500, cursor: "pointer", border: "1px solid #f0443866", color: "#f04438", borderRadius: 8, padding: "4px 10px" }}>
              Taramayı Durdur
            </button>
          )}
          <button
            onClick={async () => {
              const r = await fetch(`/api/domains/${domainId}/restart`, { method: "POST" });
              const dd = await r.json().catch(() => ({} as { message?: string; error?: string }));
              toast(r.ok ? { title: "Devam ediliyor", description: dd.message } : { title: "Devam ettirilemedi", description: dd.error, variant: "destructive" });
              queryClient.invalidateQueries({ queryKey: getGetDomainQueryKey(domainId) });
            }}
            className="bg-white dark:bg-gray-800" style={{ fontSize: 12, fontWeight: 500, cursor: "pointer", border: "1px solid #f7900966", color: "#b54708", borderRadius: 8, padding: "4px 10px" }}>
            Taramaya Devam Et
          </button>
        </div>
      )}

      {/* ── Canvas alanı (tam kalan yükseklik) ── */}
      <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <ClusterBoard
        tree={tree as import("@/components/clusters/ClusterBoard").BoardCluster[]}
        domainId={domainId}
        orphanCount={orphanPages?.length ?? 0}
        onRename={handleRename}
        onDelete={handleDelete}
        onAddCluster={async (name, parentId) => {
          // Alan adi "parentId" — sunucu ve spec boyle bekliyor. Onceden
          // "parentClusterId" gonderiliyordu; sunucu tanimadigi icin ust
          // baglanti sessizce dusuyor, her alt cluster kok olarak aciliyordu.
          await createCluster.mutateAsync({ domainId, data: { name, parentId: parentId ?? undefined } });
          invalidateAll();
        }}
        onSetPillar={handleSetPillar}
        selectedIds={selectedIds}
        onSelectToggle={(id, checked) => {
          setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(id); else next.delete(id);
            return next;
          });
        }}
        onMerge={openMerge}
        view={activeView}
        domainUrl={domainData?.url?.replace(/^https?:\/\//, "").replace(/\/$/, "") ?? ""}
        onMovePage={async (pageId, targetClusterId) => {
          try {
            await movePage.mutateAsync({ pageId, data: { clusterId: targetClusterId } });
            toast({ title: targetClusterId === null ? "Sayfa atanmamışa alındı" : "Sayfa taşındı" });
            invalidateAll();
            if (targetClusterId != null) queryClient.invalidateQueries({ queryKey: getGetClusterPagesQueryKey(targetClusterId) });
            queryClient.invalidateQueries({ queryKey: getGetDomainPagesQueryKey(domainId, { clusterId: null }) });
          } catch {
            toast({ title: "Sayfa taşınamadı", variant: "destructive" });
          }
        }}
        onMoveCluster={async (clusterId, newParentId) => {
          if (newParentId === clusterId) return;
          try {
            await updateCluster.mutateAsync({ clusterId, data: { parentId: newParentId } });
            toast({ title: newParentId === null ? "Cluster kök seviyeye taşındı" : "Cluster alt-küme olarak ayarlandı" });
            invalidateAll();
          } catch (err) {
            const detail = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
            toast({ title: detail || "Cluster taşınamadı", variant: "destructive" });
          }
        }}
        focusClusterId={focusId}
        onFocusCluster={setFocusId}
        focusPageId={focusPageId}
        onPagesLoaded={handlePagesLoaded}
        orphanPages={(orphanPages ?? []).map((p) => ({ id: p.id, url: p.url, title: p.title }))}
        onUnassignPage={async (pageId) => {
          try {
            await movePage.mutateAsync({ pageId, data: { clusterId: null } });
            toast({ title: "Sayfa atanmamışa alındı" });
            invalidateAll();
            queryClient.invalidateQueries({ queryKey: getGetDomainPagesQueryKey(domainId, { clusterId: null }) });
          } catch {
            toast({ title: "İşlem başarısız", variant: "destructive" });
          }
        }}
      />
      </div>{/* canvas area */}
    </div>{/* full screen layout */}

      {/* Orphan pages – neurol-ux'ta stats içinde gösterildiği için gizlendi */}
      {false && <div className="rounded-xl border bg-card shadow-sm" data-testid="orphans-card">
        <button
          type="button"
          onClick={() => setOrphansOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-3 text-left px-5 py-4"
          data-testid="btn-toggle-orphans"
        >
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-amber-600" />
            <span className="font-semibold">Atanmamış Sayfalar</span>
            <Badge variant="secondary">{orphanPages?.length ?? 0}</Badge>
          </div>
          {orphansOpen ? <ChevronDown className="w-5 h-5 shrink-0" /> : <ChevronRight className="w-5 h-5 shrink-0" />}
        </button>
        {orphansOpen && (
          <div className="border-t px-5 py-4 space-y-3">
            {/* Orphan arama */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
              <Input
                value={orphanSearch}
                onChange={(e) => { setOrphanSearch(e.target.value); setOrphanPage(0); }}
                placeholder="Sayfa ara (URL veya başlık)..."
                className="pl-8 h-8 text-sm"
              />
            </div>
            {(orphanPages?.length ?? 0) === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-4">Tüm sayfalar bir cluster'a atanmış.</div>
            ) : (() => {
              const filtered = (orphanPages ?? []).filter((page) => {
                if (!orphanSearch.trim()) return true;
                const q = orphanSearch.toLowerCase();
                return page.url.toLowerCase().includes(q) || (page.title ?? "").toLowerCase().includes(q);
              });
              const totalPages = Math.ceil(filtered.length / ORPHAN_PAGE_SIZE);
              const pageSlice = filtered.slice(orphanPage * ORPHAN_PAGE_SIZE, (orphanPage + 1) * ORPHAN_PAGE_SIZE);
              return (
              <div>
                {filtered.length > ORPHAN_PAGE_SIZE && (
                  <div className="flex items-center justify-between mb-2 text-xs text-muted-foreground">
                    <span>{filtered.length} sayfa • {orphanPage * ORPHAN_PAGE_SIZE + 1}–{Math.min((orphanPage + 1) * ORPHAN_PAGE_SIZE, filtered.length)} gösteriliyor</span>
                    <div className="flex gap-1">
                      <button onClick={() => setOrphanPage(p => Math.max(0, p - 1))} disabled={orphanPage === 0} className="px-2 py-0.5 rounded border disabled:opacity-40">‹</button>
                      <span className="px-2 py-0.5">{orphanPage + 1}/{totalPages}</span>
                      <button onClick={() => setOrphanPage(p => Math.min(totalPages - 1, p + 1))} disabled={orphanPage >= totalPages - 1} className="px-2 py-0.5 rounded border disabled:opacity-40">›</button>
                    </div>
                  </div>
                )}
              <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                {pageSlice.map((page) => (
                  <div key={page.id} className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <DraggablePageRow
                        page={page}
                        clusterId={null}
                        isSelected={selectedPageIds.has(page.id)}
                        onSelectToggle={(checked) => togglePageSelect(page.id, checked)}
                        onToggleFeatured={() => handleToggleFeatured(page.id, !page.isFeatured)}
                        featuredPending={featuredPendingPageId === page.id}
                      />
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <OrphanClusterSelect
                        clusters={allClusters}
                        value={orphanAssignTarget[page.id] ?? ""}
                        onChange={(v) => setOrphanAssignTarget((prev) => ({ ...prev, [page.id]: v }))}
                        pageId={page.id}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8"
                        disabled={!orphanAssignTarget[page.id] || movePage.isPending}
                        onClick={async () => {
                          const cId = Number(orphanAssignTarget[page.id]);
                          if (!cId) return;
                          await movePage.mutateAsync({ pageId: page.id, data: { clusterId: cId } });
                          invalidateAll();
                        }}
                        data-testid={`orphan-assign-btn-${page.id}`}
                      >
                        <Check className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              </div>
            );})()}
          </div>
        )}
      </div>}

    {/* Page selection floating bar */}
    {selectedPageIds.size > 0 && (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 rounded-full border bg-card shadow-lg px-4 py-2 flex items-center gap-3" data-testid="page-selection-bar">
        <span className="text-sm font-medium">{selectedPageIds.size} sayfa seçildi</span>
        <Button size="sm" variant="ghost" onClick={clearPageSelection} className="h-8">Temizle</Button>
        <Button size="sm" onClick={openPageMergeDialog} data-testid="btn-pages-to-subcluster" className="h-8">
          <FolderTree className="w-4 h-4 mr-1" />
          Yeni alt-kümede grupla
        </Button>
      </div>
    )}

    {/* Dialogs */}
    <Dialog open={pageMergeOpen} onOpenChange={setPageMergeOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Seçilen sayfalardan alt-küme oluştur</DialogTitle>
          <DialogDescription>{selectedPageIds.size} sayfa yeni bir alt-kümede gruplanacak.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Input value={pageMergeName} onChange={(e) => setPageMergeName(e.target.value)} placeholder="Alt-küme adı..." autoFocus data-testid="input-page-merge-name" onKeyDown={(e) => { if (e.key === "Enter") submitPageMerge(); }} />
          <Select value={pageMergeParentId} onValueChange={setPageMergeParentId}>
            <SelectTrigger data-testid="select-page-merge-parent"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__root">Kök seviye</SelectItem>
              {allClusters.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.parentId != null ? "↳ " : ""}{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setPageMergeOpen(false)}>İptal</Button>
          <Button onClick={submitPageMerge} disabled={createClusterFromPages.isPending} data-testid="btn-confirm-page-merge">
            {createClusterFromPages.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FolderTree className="w-4 h-4 mr-2" />}
            Oluştur
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cluster'ları Birleştir</DialogTitle>
          <DialogDescription>Seçilen {selectedIds.size} cluster tek bir yeni cluster'a birleştirilecek.</DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Input value={mergeName} onChange={(e) => setMergeName(e.target.value)} placeholder="Yeni cluster adı" data-testid="input-merge-name" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setMergeOpen(false)}>İptal</Button>
          <Button onClick={submitMerge} disabled={mergeClusters.isPending} data-testid="btn-confirm-merge">
            {mergeClusters.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Combine className="w-4 h-4 mr-2" />}
            Birleştir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={!!addSubFor} onOpenChange={(o) => !o && setAddSubFor(null)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>"{addSubFor?.name}" altına alt-küme ekle</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-2">
            <Button type="button" variant={addSubMode === "new" ? "default" : "outline"} onClick={() => setAddSubMode("new")} className="w-full" data-testid="add-sub-mode-new"><Plus className="w-4 h-4 mr-2" />Yeni oluştur</Button>
            <Button type="button" variant={addSubMode === "existing" ? "default" : "outline"} onClick={() => setAddSubMode("existing")} className="w-full" disabled={eligibleExistingClusters.length === 0} data-testid="add-sub-mode-existing"><Move className="w-4 h-4 mr-2" />Mevcut cluster'ı taşı</Button>
          </div>
          {addSubMode === "new" ? (
            <Input value={addSubName} onChange={(e) => setAddSubName(e.target.value)} placeholder="Alt-küme adı..." data-testid="input-add-sub-name" autoFocus onKeyDown={(e) => { if (e.key === "Enter") submitAddSub(); }} />
          ) : (
            <Select value={addSubPick} onValueChange={setAddSubPick}>
              <SelectTrigger data-testid="select-add-sub-pick"><SelectValue placeholder="Bir cluster seç..." /></SelectTrigger>
              <SelectContent>
                {eligibleExistingClusters.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}{c.parentId != null ? " (alt-küme)" : ""} • {c.pageCount} sayfa</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setAddSubFor(null)}>İptal</Button>
          <Button onClick={submitAddSub} disabled={createCluster.isPending || updateCluster.isPending} data-testid="btn-confirm-add-sub">
            {createCluster.isPending || updateCluster.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Ekle
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <DragOverlay>
      {activeDrag ? (
        <div className="px-3 py-2 rounded-md border bg-card shadow-lg flex items-center gap-2">
          {activeDrag.kind === "page" ? <FileText className="w-4 h-4 text-muted-foreground" /> : <Move className="w-4 h-4 text-muted-foreground" />}
          <span className="text-sm font-medium truncate max-w-[260px]">{activeDrag.label}</span>
        </div>
      ) : null}
    </DragOverlay>

    <AlertDialog open={deleteClusterId != null} onOpenChange={(o) => { if (!o) setDeleteClusterId(null); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Cluster'ı sil</AlertDialogTitle>
          <AlertDialogDescription>Bu cluster'ı silmek istediğinize emin misiniz? İçindeki sayfalar atamasız olarak listelenecek.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="btn-cancel-delete-cluster">Vazgeç</AlertDialogCancel>
          <AlertDialogAction onClick={confirmDelete} data-testid="btn-confirm-delete-cluster">Sil</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <AlertDialog open={recrawlOpen} onOpenChange={setRecrawlOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Yeniden tara</AlertDialogTitle>
          <AlertDialogDescription>Cluster yapısını koruyarak siteyi yeniden taramak istiyor musun?</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="btn-cancel-recrawl">Vazgeç</AlertDialogCancel>
          <AlertDialogAction onClick={confirmSaveRecrawl} data-testid="btn-confirm-recrawl">Devam et</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    </DndContext>
  );
}